import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, TrendingDown, AlertTriangle, Shield, Target, 
  DollarSign, Users, Globe, Zap, FileDown, ChevronRight,
  BarChart3, AlertCircle, CheckCircle2, ArrowUpRight, Info, ChevronDown
} from 'lucide-react';
import { generatePDFReport } from '@/services/pdfExportService';
import { FeedbackWidget } from '@/components/common/FeedbackWidget';
import { trackEvent } from '@/hooks/useAnalytics';
import { authFetch } from '@/utils/authFetch';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Risk Level Badge Component
const RiskBadge = ({ level }) => {
  const config = {
    low: { bg: 'bg-stable/20', text: 'text-stable', border: 'border-stable/30', label: 'Low' },
    medium: { bg: 'bg-warning/20', text: 'text-warning', border: 'border-warning/30', label: 'Medium' },
    high: { bg: 'bg-destructive/20', text: 'text-destructive', border: 'border-destructive/30', label: 'High' },
  };
  const c = config[level] || config.medium;
  return (
    <Badge className={`${c.bg} ${c.text} ${c.border} border font-semibold`}>
      {c.label} Risk
    </Badge>
  );
};

// Format currency
const formatCurrency = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};

// Executive Snapshot Section
const ExecutiveSnapshot = ({ data }) => {
  const brand = data?.brand_metrics || {};
  const eco = data?.economic_value || {};
  
  const metrics = [
    {
      label: 'Estimated Brand Value Range',
      value: `${formatCurrency(eco.range_low || 240000000)} – ${formatCurrency(eco.range_high || 340000000)}`,
      sublabel: 'Directional strategic indicator',
      highlight: true,
    },
    {
      label: 'Overall Brand Score',
      value: brand.overall_brand_score || 71,
      sublabel: 'Composite index (0-100)',
    },
    {
      label: 'Year-over-Year Growth',
      value: `+${eco.year_over_year_change || 18.5}%`,
      sublabel: 'vs. prior cycle',
      positive: true,
    },
    {
      label: 'CONCACAF Rank',
      value: '#1',
      sublabel: 'Confederation position',
    },
    {
      label: 'Risk Assessment',
      value: 'Medium',
      sublabel: 'Portfolio risk level',
      badge: 'medium',
    },
    {
      label: '2026 Base Case Impact',
      value: '+$50M',
      sublabel: 'Host premium projection',
      positive: true,
    },
  ];

  return (
    <section className="mb-10">
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="w-6 h-6 text-primary" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Executive Snapshot</h2>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {metrics.map((metric, i) => (
          <Card key={i} className={`bg-card border-border/50 ${metric.highlight ? 'lg:col-span-2' : ''}`}>
            <CardContent className="p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{metric.label}</p>
              {metric.badge ? (
                <div className="mb-1">
                  <RiskBadge level={metric.badge} />
                </div>
              ) : (
                <p className={`text-2xl font-bold ${metric.positive ? 'text-stable' : 'text-foreground'} ${metric.highlight ? 'text-3xl' : ''}`}>
                  {metric.value}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">{metric.sublabel}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

// Strategic Risks Section
const StrategicRisks = () => {
  const risks = [
    {
      id: 'player-dependency',
      title: 'Player Dependency Risk',
      icon: Users,
      level: 'medium',
      impact: '-$15M to -$25M',
      impactLabel: 'Estimated brand impact if key player unavailable',
      description: 'Christian Pulisic represents an estimated ~22% of aggregate roster brand value (modeled). Extended injury or form decline during 2026 cycle creates significant concentration exposure.',
      recommendation: 'Accelerate next-generation player brand development. Diversify media exposure across top-5 roster players.',
    },
    {
      id: 'tournament-performance',
      title: 'Tournament Underperformance Risk',
      icon: AlertTriangle,
      level: 'high',
      impact: '-$40M to -$60M',
      impactLabel: 'Estimated brand impact if group stage exit in 2026',
      description: 'A group stage exit in World Cup 2026 as co-host would represent the most significant brand value destruction scenario. Historical precedent: Brazil 2014 semi-final loss resulted in ~18% brand value decline.',
      recommendation: 'Implement performance contingency communications strategy. Prepare sponsor relationship management protocols for underperformance scenarios.',
    },
    {
      id: 'media-sentiment',
      title: 'Media Sentiment Risk',
      icon: Globe,
      level: 'low',
      impact: '-$5M to -$10M',
      impactLabel: 'Estimated brand impact from sustained negative coverage',
      description: 'Modeled media sentiment baseline at 72/100 (positive, pending live integration). No significant controversies identified in public signals. Social media engagement metrics stable with estimated 12% YoY growth.',
      recommendation: 'Maintain proactive media relations. Continue athlete media training program. Monitor emerging narrative risks quarterly.',
    },
  ];

  return (
    <section className="mb-10">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-6 h-6 text-destructive" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Top 3 Strategic Risks</h2>
      </div>
      
      <div className="grid md:grid-cols-3 gap-5">
        {risks.map((risk) => (
          <Card key={risk.id} className="bg-card border-border/50 hover:border-border transition-colors">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted/50">
                    <risk.icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-sm font-semibold">{risk.title}</CardTitle>
                </div>
                <RiskBadge level={risk.level} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/10">
                <p className="text-lg font-bold text-destructive">{risk.impact}</p>
                <p className="text-xs text-muted-foreground">{risk.impactLabel}</p>
              </div>
              
              <p className="text-sm text-muted-foreground leading-relaxed">
                {risk.description}
              </p>
              
              <div className="pt-3 border-t border-border/50">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Recommendation</p>
                <p className="text-sm text-foreground">{risk.recommendation}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

// Strategic Opportunities Section
const StrategicOpportunities = () => {
  const opportunities = [
    {
      id: 'sponsorship-whitespace',
      title: 'Sponsorship Whitespace Opportunity',
      icon: DollarSign,
      upside: '+$20M to +$35M',
      upsideLabel: 'Estimated incremental annual revenue (directional)',
      description: 'Estimated sponsorship portfolio benchmarks at approximately 78% of top European federation equivalents (modeled). Potential for category depth optimization and new vertical expansion, subject to federation portfolio validation.',
      recommendation: 'Evaluate category depth potential and rate optimization across existing and prospective sponsor verticals ahead of 2026 cycle. Leverage host country premium for enhanced deal terms.',
      timeframe: '12-18 months',
    },
    {
      id: 'host-premium',
      title: '2026 Host Premium',
      icon: Target,
      upside: '+$50M to +$65M',
      upsideLabel: 'One-time brand value uplift',
      description: 'Co-host status provides unprecedented domestic media exposure opportunity. Historical host premium analysis (Russia 2018, Brazil 2014) suggests 35-50% brand value increase during host cycles (directional estimate based on public precedent).',
      recommendation: 'Maximize owned media content production. Negotiate broadcast partner integration packages. Launch 2026-specific merchandise licensing program by Q2 2025.',
      timeframe: '18-24 months',
    },
    {
      id: 'digital-acceleration',
      title: 'Digital Acceleration',
      icon: Zap,
      upside: '+$10M to +$18M',
      upsideLabel: 'Digital revenue opportunity',
      description: 'Current digital monetization at 42% of English FA benchmark. TikTok engagement velocity 2.3x YoY. NFT/Web3 revenue streams unexplored.',
      recommendation: 'Implement direct-to-consumer content subscription model. Launch player-specific content verticals. Evaluate blockchain-based fan engagement platform pilot.',
      timeframe: '6-12 months',
    },
  ];

  return (
    <section className="mb-10">
      <div className="flex items-center gap-3 mb-6">
        <ArrowUpRight className="w-6 h-6 text-stable" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Top 3 Strategic Opportunities</h2>
      </div>
      
      <div className="grid md:grid-cols-3 gap-5">
        {opportunities.map((opp) => (
          <Card key={opp.id} className="bg-card border-border/50 hover:border-border transition-colors">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-stable/10">
                    <opp.icon className="w-5 h-5 text-stable" />
                  </div>
                  <CardTitle className="text-sm font-semibold">{opp.title}</CardTitle>
                </div>
                <Badge className="bg-muted/50 text-muted-foreground border-border/50 text-xs">
                  {opp.timeframe}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-stable/5 border border-stable/10">
                <p className="text-lg font-bold text-stable">{opp.upside}</p>
                <p className="text-xs text-muted-foreground">{opp.upsideLabel}</p>
              </div>
              
              <p className="text-sm text-muted-foreground leading-relaxed">
                {opp.description}
              </p>
              
              <div className="pt-3 border-t border-border/50">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Activation Recommendation</p>
                <p className="text-sm text-foreground">{opp.recommendation}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

// 2026 Strategic Projection Section
const StrategicProjection = () => {
  const scenarios = [
    {
      outcome: 'Group Stage Exit',
      probability: '10%',
      impact: '-$40M',
      impactType: 'negative',
      description: 'Brand value decline from unmet host expectations',
    },
    {
      outcome: 'Round of 32',
      probability: '15%',
      impact: '+$10M',
      impactType: 'neutral',
      description: 'Minimum knockout advancement; modest uplift',
    },
    {
      outcome: 'Round of 16',
      probability: '30%',
      impact: '+$25M',
      impactType: 'neutral',
      description: 'Meets baseline expectations for co-host',
    },
    {
      outcome: 'Quarterfinals',
      probability: '25%',
      impact: '+$50M',
      impactType: 'positive',
      description: 'Base case projection; meets market expectations',
    },
    {
      outcome: 'Semifinals',
      probability: '14%',
      impact: '+$125M',
      impactType: 'positive',
      description: 'Significant brand acceleration; historic achievement',
    },
    {
      outcome: 'Final / Champions',
      probability: '6%',
      impact: '+$250M',
      impactType: 'positive',
      description: 'Transformational brand value event',
    },
  ];

  return (
    <section className="mb-10">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="w-6 h-6 text-primary" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">2026 Strategic Projection Summary</h2>
      </div>
      
      <Card className="bg-card border-border/50">
        <CardContent className="p-6">
          <div className="mb-6">
            <p className="text-sm text-muted-foreground mb-4">
              Tournament outcome sensitivity analysis for FIFA World Cup 2026 (48-team format). Brand value impact projections based on historical 
              host country performance data and current market conditions.
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tournament Outcome</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Probability</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Brand Value Impact</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Assessment</th>
                </tr>
              </thead>
              <tbody>
                {scenarios.map((s, i) => (
                  <tr key={i} className="border-b border-border/30 last:border-0 hover:bg-muted/20 transition-colors">
                    <td className="py-4 px-4">
                      <span className="font-semibold text-foreground">{s.outcome}</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-muted-foreground">{s.probability}</span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className={`text-lg font-bold ${
                        s.impactType === 'positive' ? 'text-stable' : 
                        s.impactType === 'negative' ? 'text-destructive' : 'text-warning'
                      }`}>
                        {s.impact}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-muted-foreground">{s.description}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-border/50">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Methodology Note</p>
                <p className="text-xs text-muted-foreground">
                  Projections derived from weighted composite model incorporating historical host country tournament data, 
                  current sponsorship portfolio dynamics, and global media market conditions. Figures represent directional 
                  strategic indicators, not certified valuations. Subject to market condition variance.
                  Probabilities are directional assumptions based on historical precedent — not statistical model outputs. 
                  They represent mutually exclusive tournament outcomes (sum to 100%).
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

// Strategic Impact Summary — synthesizes scenario analysis into a directional decision-support block
const StrategicImpactSummary = () => (
  <section className="mb-10" data-testid="strategic-impact-summary">
    <div className="flex items-center gap-3 mb-6">
      <Zap className="w-6 h-6 text-primary" />
      <h2 className="text-lg font-semibold text-foreground tracking-tight">Strategic Impact Summary</h2>
    </div>

    <Card className="bg-card border-border/50">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="p-4 rounded-lg bg-stable/5 border border-stable/20">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Base Case (Quarterfinal)</p>
            <p className="text-2xl font-bold text-stable">+$50M</p>
            <p className="text-xs text-muted-foreground mt-1">Estimated brand value impact</p>
          </div>
          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Upside Case (Semifinal+)</p>
            <p className="text-2xl font-bold text-primary">+$125M to +$250M</p>
            <p className="text-xs text-muted-foreground mt-1">Historic to transformational range</p>
          </div>
          <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Downside Risk (Group Exit)</p>
            <p className="text-2xl font-bold text-destructive">-$40M</p>
            <p className="text-xs text-muted-foreground mt-1">Brand value decline from unmet expectations</p>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-muted/20 border border-border/30 mb-4">
          <p className="text-sm text-foreground leading-relaxed">
            Under the base-case scenario (quarterfinal advancement, 25% probability), the modeled outcome 
            suggests a favorable return profile for the 2026 tournament cycle. Co-host status provides a 
            structural floor on media and commercial exposure regardless of tournament performance. 
            Scenarios above base case materially strengthen the federation's negotiating position for the 
            2027-2030 commercial cycle.
          </p>
        </div>

        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <p>
            This is a directional pilot insight based on modeled assumptions, not a certified valuation 
            or investment recommendation. Impact figures are estimated ranges subject to tournament outcome, 
            market conditions, and federation-specific commercial data calibration.
          </p>
        </div>
      </CardContent>
    </Card>
  </section>
);

// Main Executive Dashboard Component
export default function USSoccerExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const res = await authFetch('/api/national-team/usmnt');
        if (!res.ok) { setLoading(false); return; }
        const team = await res.json();
        setData(team);
      } catch (e) {
        console.error('Error loading data:', e);
      }
      setLoading(false);
    }
    loadData();
  }, []);

  const handleExportPDF = () => {
    if (data) {
      trackEvent('export_pdf_click', { report: 'executive-dashboard' });
      generatePDFReport(data, 'executive-dashboard', {
        teamId: 'usmnt',
        isDemoMode: false
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading executive dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      {/* Print-specific styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: white !important; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
        }
      `}</style>
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className="bg-primary/10 text-primary border-primary/20">CONFIDENTIAL</Badge>
                <Badge className="bg-muted/50 text-muted-foreground border-border/50">USMNT</Badge>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                Executive Strategic Dashboard
              </h1>
              <p className="text-muted-foreground mt-2">
                U.S. Soccer Federation | Board-Level Intelligence Summary
              </p>
            </div>
            
            <div className="flex items-center gap-3 no-print">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExportPDF}
                className="gap-2"
              >
                <FileDown className="w-4 h-4" />
                Export PDF
              </Button>
            </div>
          </div>
          
          {/* Assessment Date */}
          <div className="mb-8 p-4 rounded-lg bg-muted/30 border border-border/50">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">Assessment Date: </span>
                <span className="font-medium text-foreground">
                  {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Data Source: </span>
                <span className="font-medium text-foreground">YBV Brand Intelligence Platform</span>
              </div>
            </div>
          </div>
          
          {/* Sections */}
          <ExecutiveSnapshot data={data} />
          
          {/* Brand Dependency Risk Summary Card */}
          <Card className="mb-10 bg-destructive/5 border-destructive/20 no-print" data-testid="brand-dependency-summary">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-destructive/10">
                    <Shield className="w-5 h-5 text-destructive" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">Brand Dependency Risk</h3>
                      <Badge className="bg-destructive/20 text-destructive border-destructive/30 border font-semibold">
                        HIGH
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      35% of team brand value concentrated in 1 player (Pulisic)
                    </p>
                  </div>
                </div>
                <Link to="/us-soccer/brand-dependency">
                  <Button variant="outline" size="sm" className="gap-2">
                    View Full Analysis
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
          
          <div className="print-break" />
          
          <StrategicRisks />
          
          <StrategicOpportunities />
          
          <div className="print-break" />
          
          <StrategicProjection />
          
          <StrategicImpactSummary />
          
          {/* Footer Disclaimer — compact two-layer model */}
          <DisclaimerCompact />
        </div>
      </main>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <FeedbackWidget route="/us-soccer/executive-dashboard" />
      </div>
    </div>
  );
}

function DisclaimerCompact() {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div className="mt-10 border-t border-border/30 pt-5" data-testid="exec-disclaimer">
      <div className="flex items-start gap-2">
        <Info className="w-3.5 h-3.5 text-muted-foreground/50 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-muted-foreground/70 leading-relaxed">
            Estimated metrics based on available data and proprietary modelling. Not audited financial valuations. For internal strategic use only.
            <button
              onClick={() => setExpanded(!expanded)}
              className="ml-1.5 text-primary/60 hover:text-primary transition-colors inline-flex items-center gap-0.5"
              data-testid="disclaimer-toggle"
            >
              {expanded ? 'Hide details' : 'View full disclaimer'}
              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            </button>
          </p>
          {expanded && (
            <div className="mt-3 text-[11px] text-muted-foreground/50 leading-relaxed space-y-2">
              <p>
                The valuations, projections, and strategic assessments presented in this dashboard are directional
                indicators derived from publicly available data signals and proprietary analytical models. They do not
                constitute financial advice, audited appraisals, or certified valuations. All figures are subject to
                variation based on market conditions, tournament outcomes, and other external factors.
              </p>
              <p>
                This document is intended solely for internal strategic planning and executive decision-making purposes
                within the authorized recipient organization. Distribution beyond authorized recipients requires written consent.
              </p>
              <div className="pt-2 border-t border-border/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <span><span className="font-semibold text-primary/60">YBV</span> Brand Intelligence Platform</span>
                <span>ISO 10668 Aligned | BSI-Aligned Methodology | Interbrand-Aligned Valuation</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
